import { isAttachmentStrategyUsingCheckoutButtons } from "@redotech/redo-model/returns/attachment-strategy";
import { parseGid } from "@redotech/redo-model/shopify/gid-utils";
import {
  feeProduct,
  WidgetAutocheck,
  WidgetConfig,
  WidgetFees,
} from "@redotech/redo-model/widget";
import { ShopifyCart } from "@redotech/shopify-client/ajax";
import { useEffect, useState } from "react";
import { StorefrontCoverageHtmlElement } from "../cart-app";
import { log } from "../monitoring/log";
import {
  DEFAULT_CHECKOUT_BUTTONS_SELECTOR,
  DEFAULT_PRODUCT_SELECTORS,
} from "../site-config/selectors";
import { getCartShadowRoot } from "../utils";
import { hasValidButtonSelectors } from "./checkout-button-utils";

export const useHideRedoProductAndCheckoutButtons = (
  autocheck: WidgetAutocheck,
  fees: WidgetFees,
  toggles: {
    redoHtmlElements: StorefrontCoverageHtmlElement[];
    widgetConfig?: WidgetConfig;
  }[],
  cart: ShopifyCart | undefined,
) => {
  const [foundValidButtonSelectors, setFoundValidButtonSelectors] = useState<
    boolean | undefined
  >(undefined);
  const redoIndex = cart?.items.findIndex((item) =>
    [
      Number(fees.packageProduct?.productId),
      Number(fees.returnProduct?.productId),
      Number(fees.bothProduct?.productId),
    ].includes(Number(item.product_id)),
  );

  useEffect(() => {
    if (!autocheck.attachmentStrategy) {
      return;
    }

    if (
      !isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy]
    ) {
      return;
    }

    const valid = hasValidButtonSelectors(toggles);
    if (foundValidButtonSelectors !== valid) {
      setFoundValidButtonSelectors(valid);
    }
  }, [toggles, autocheck.attachmentStrategy, foundValidButtonSelectors]);

  useEffect(() => {
    if (
      !fees ||
      !autocheck.hideRedoProduct?.enabled ||
      autocheck.disableRedoForCustomer
    ) {
      return;
    }

    if (
      redoStorefront?.type !== "commercecloud" &&
      isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
      foundValidButtonSelectors === false
    ) {
      return;
    }

    const products = [
      fees.packageProduct,
      fees.returnProduct,
      fees.bothProduct,
    ].filter(Boolean) as feeProduct[];

    const productSelectorTemplates = autocheck.hideRedoProduct.productSelector
      ? [autocheck.hideRedoProduct.productSelector]
      : DEFAULT_PRODUCT_SELECTORS;

    const productAndCheckoutButtonSelectorTemplates = [
      ...productSelectorTemplates,
      ...getCheckoutButtonSelectorTemplatesIfReady({
        autocheck,
        foundValidButtonSelectors: foundValidButtonSelectors ?? false,
      }),
    ];

    const selector = expandProductSelector({
      selectorTemplates: productAndCheckoutButtonSelectorTemplates,
      vars: {
        products,
        redoIndex: redoIndex ? String(redoIndex + 1) : "1000000",
      },
    });
    if (!selector) {
      return;
    }
    const hideElementsCss = `${selector} {
      display: none !important;
      max-width: 0 !important;
      max-height: 0 !important;
      overflow: hidden !important;
      padding: 0 !important;
      margin: 0 !important;
      border: 0 !important;
      visibility: hidden !important;
    }`.replaceAll("\n", "");
    const style = document.createElement("style");
    style.innerText = hideElementsCss;
    document.head.append(style);

    // If the cart is inside a shadow root, append the styles there
    const shadowRoot = getCartShadowRoot(autocheck);

    let shadowStyle: HTMLStyleElement | undefined = undefined;
    if (shadowRoot) {
      shadowStyle = document.createElement("style");
      shadowStyle.innerText = hideElementsCss;
      shadowRoot.append(shadowStyle);
    }

    return () => {
      style.remove();
      shadowStyle?.remove();
    };
  }, [
    fees,
    autocheck,
    autocheck.hideRedoProduct?.enabled,
    foundValidButtonSelectors,
    cart,
    redoIndex,
  ]);
};

const isValidCssSelector = (selector: string): boolean => {
  if (!selector) {
    return false;
  }
  try {
    // Some selector templates like .%handle% can generate an invalid selector like .163 if the handle is a number
    document.createDocumentFragment().querySelector(selector);
    return true;
  } catch (error) {
    return false;
  }
};

type ProductSelectorVars = { products: feeProduct[]; redoIndex: string };
const expandProductSelector = ({
  selectorTemplates,
  vars,
}: {
  selectorTemplates: string[];
  vars: ProductSelectorVars;
}): string => {
  const { products, redoIndex } = vars;

  const selectors = new Set<string>();

  for (const selectorTemplate of selectorTemplates) {
    for (const product of products) {
      if (!product) {
        continue;
      }
      const variants =
        product.variants.length >= 1 ? product.variants : [{ variant_id: "" }];
      for (const variant of variants) {
        selectors.add(
          selectorTemplate
            .replaceAll("%handle%", product.handle || "")
            ?.replaceAll("%product_id%", String(product.productId) || "")
            ?.replaceAll(
              "%variant_id%",
              product.variants?.length >= 1
                ? parseGid(String(variant.variant_id))
                : "",
            )
            ?.replaceAll("%index%", redoIndex),
        );
      }
    }
  }
  const selector = Array.from(selectors).filter(isValidCssSelector).join(", ");
  return selector;
};

const getCheckoutButtonSelectorTemplatesIfReady = ({
  autocheck,
  foundValidButtonSelectors,
}: {
  autocheck: WidgetAutocheck;
  foundValidButtonSelectors: boolean;
}): string[] => {
  if (
    autocheck.hideRedoProduct?.checkoutButtonSelector &&
    !isValidCssSelector(autocheck.hideRedoProduct?.checkoutButtonSelector) &&
    !autocheck.hideRedoProduct?.checkoutButtonSelector.includes("%")
  ) {
    log(
      `Invalid hide checkout buttons selector configured: ${autocheck?.hideRedoProduct?.checkoutButtonSelector} - this may result in double checkout buttons.`,
    );
  }

  if (
    isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
    foundValidButtonSelectors !== false
  ) {
    return [
      autocheck.hideRedoProduct?.checkoutButtonSelector ||
        DEFAULT_CHECKOUT_BUTTONS_SELECTOR,
    ];
  }
  return [];
};
